import { Building2, Users, MessageSquare, Calendar, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

const stats = [
  {
    label: "Total de Imóveis",
    value: "0",
    icon: Building2,
    change: "0%",
    changeType: "neutral",
  },
  {
    label: "Proprietários",
    value: "0",
    icon: Users,
    change: "0%",
    changeType: "neutral",
  },
  {
    label: "Mensagens Enviadas",
    value: "0",
    icon: MessageSquare,
    change: "0%",
    changeType: "neutral",
  },
  {
    label: "Agendamentos",
    value: "0",
    icon: Calendar,
    change: "0%",
    changeType: "neutral",
  },
];

const Index = () => {
  const navigate = useNavigate();

  const { data: properties = [] } = useQuery({
    queryKey: ['properties'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data } = await supabase
        .from('properties')
        .select('*')
        .eq('user_id', user.id);
      
      return data || [];
    }
  });

  const { data: owners = [] } = useQuery({
    queryKey: ['owners'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id);
      
      return data || [];
    }
  });

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="p-4 md:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm md:text-base text-gray-600">Bem-vindo ao seu painel de controle</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="p-4 md:p-6 bg-white rounded-xl shadow-card hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="p-2 bg-primary/10 rounded-lg">
                <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              </div>
              <span className={cn(
                "text-xs md:text-sm font-medium flex items-center gap-1",
                stat.changeType === "increase" ? "text-green-600" : 
                stat.changeType === "decrease" ? "text-red-600" : 
                "text-gray-600"
              )}>
                {stat.change}
                <ArrowUpRight className="w-3 h-3 md:w-4 md:h-4" />
              </span>
            </div>
            <h3 className="mt-4 text-xl md:text-2xl font-bold text-gray-900">{stat.value}</h3>
            <p className="mt-1 text-xs md:text-sm text-gray-600">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="p-4 md:p-6 bg-white rounded-xl shadow-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-base md:text-lg font-heading font-semibold text-gray-900">Imóveis Recentes</h2>
              <p className="mt-1 text-xs md:text-sm text-gray-600">Últimos imóveis cadastrados no sistema</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/imoveis')}
              className="text-xs md:text-sm"
            >
              Ver todos
            </Button>
          </div>
          {properties.length > 0 ? (
            <div className="space-y-4">
              {properties.slice(0, 5).map((property) => (
                <div
                  key={property.id}
                  className="p-3 md:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/imoveis/${property.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h3 className="font-medium text-sm md:text-base text-gray-900">{property.title}</h3>
                      <div className="flex items-center text-xs md:text-sm text-gray-600">
                        <MapPin className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                        {property.address}
                      </div>
                    </div>
                    <span className="text-xs md:text-sm text-primary font-medium">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(property.price)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">Nenhum imóvel cadastrado ainda</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/imoveis/novo')}
                className="mt-4"
              >
                Cadastrar Imóvel
              </Button>
            </div>
          )}
        </div>

        <div className="p-4 md:p-6 bg-white rounded-xl shadow-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-base md:text-lg font-heading font-semibold text-gray-900">Proprietários</h2>
              <p className="mt-1 text-xs md:text-sm text-gray-600">Proprietários cadastrados</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/proprietarios')}
              className="text-xs md:text-sm"
            >
              Ver todos
            </Button>
          </div>
          {owners.length > 0 ? (
            <div className="space-y-4">
              {owners.map((owner) => (
                <div
                  key={owner.id}
                  className="p-3 md:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/proprietarios/${owner.id}`)}
                >
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm md:text-base text-gray-900">{owner.name}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs md:text-sm text-gray-600">
                      <div className="flex items-center">
                        <Mail className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                        <span className="truncate">{owner.email}</span>
                      </div>
                      <div className="flex items-center">
                        <Phone className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                        <span className="truncate">{owner.phone}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">Nenhum proprietário cadastrado ainda</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/proprietarios/novo')}
                className="mt-4"
              >
                Cadastrar Proprietário
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Index;